import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, AlertTriangle, AlertOctagon, Info, Search, 
  Filter, CheckCircle2, MoreVertical, ShieldAlert, Zap, Clock
} from 'lucide-react';

const MOCK_ALERTS = [
  { id: 'AL-901', type: 'critical', org: 'Bộ Công an (C12)', title: 'Mất kết nối Node Đồng bộ Chính', time: 'Vừa xong', message: 'Hệ thống CSDL Quốc gia về dân cư không phản hồi ping trong 3 phút qua. Đã kích hoạt kịch bản Failover.', read: false },
  { id: 'AL-902', type: 'warning', org: 'UBND TP. Hà Nội', title: 'Tỷ lệ Lỗi Dữ liệu vượt ngưỡng', time: '15 phút trước', message: 'Phát hiện 12,400 bản ghi vi phạm cấu trúc (Schema Validations) ở đợt Push lúc 10:00.', read: false },
  { id: 'AL-903', type: 'critical', org: 'Hệ thống Auth (SSO)', title: 'Tấn công Brute Force', time: '1 giờ trước', message: 'Hệ thống IDS phát hiện 800+ lượt thử đăng nhập thất bại từ dải IP 103.11.x.x. Đã ban IP tự động.', read: true },
  { id: 'AL-904', type: 'info', org: 'Bộ Tài chính', title: 'Cập nhật Schema Thành công', time: '3 giờ trước', message: 'Phiên bản Data Model v2.1.4 (Thuế vụ) đã được áp dụng tự động mà không gặp xung đột.', read: true },
  { id: 'AL-905', type: 'warning', org: 'Bộ Y tế', title: 'Hồ sơ sắp hết hạn khắc phục', time: '5 giờ trước', message: 'Đơn vị BYT còn 24 giờ để hoàn thiện 15 Ticket khắc phục trước khi bị khoá cổng API tạm thời.', read: true },
];

const STAT_CONFIG = [
  { label: 'Chưa đọc', value: 2, icon: Bell, bg: 'bg-indigo-500/10', color: 'text-indigo-600 dark:text-indigo-400', borderColor: 'border-indigo-500/30' },
  { label: 'Nguy cấp', value: 2, icon: AlertOctagon, bg: 'bg-rose-500/10', color: 'text-rose-600 dark:text-rose-400', borderColor: 'border-rose-500/30' },
  { label: 'Cảnh báo', value: 2, icon: AlertTriangle, bg: 'bg-amber-500/10', color: 'text-amber-600 dark:text-amber-400', borderColor: 'border-amber-500/30' },
  { label: 'Đã xử lý AI', value: 184, icon: Zap, bg: 'bg-emerald-500/10', color: 'text-emerald-600 dark:text-emerald-400', borderColor: 'border-emerald-500/30' },
];

export default function AlertsPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [alerts, setAlerts] = useState(MOCK_ALERTS);

  const filterAlerts = alerts.filter(a => activeFilter === 'all' || a.type === activeFilter);
  
  const getIcon = (type: string) => {
    switch (type) {
      case 'critical': return <AlertOctagon size={24} className="text-rose-500 dark:text-rose-400" />;
      case 'warning': return <AlertTriangle size={24} className="text-amber-500 dark:text-amber-400" />;
      case 'info': return <Info size={24} className="text-cyan-500 dark:text-cyan-400" />;
      default: return <Bell size={24} className="text-slate-500 dark:text-slate-400" />;
    }
  };

  const markAllRead = () => {
    setAlerts(alerts.map(a => ({ ...a, read: true })));
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative pb-10">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
         <div>
            <h2 className="text-3xl font-heading font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3 leading-tight">
              <ShieldAlert className="text-amber-500" size={32} />
              Trung tâm Cảnh báo
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-2xl font-medium">
              Cập nhật tức thời các rủi ro hạ tầng, lỗi vi phạm dữ liệu và thông báo từ hệ thống AI giám sát theo thời gian thực.
            </p>
         </div>
         <div className="flex gap-3">
            <button 
               onClick={markAllRead}
               className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 text-[10px] font-black tracking-widest hover:bg-slate-50 dark:hover:bg-white/5 transition-all shadow-sm active:scale-95"
            >
               <CheckCircle2 size={16} /> Đánh dấu đã đọc tất cả
            </button>
         </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CONFIG.map((stat, idx) => (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            key={idx} 
            className={`glass-panel p-5 flex items-center gap-4 border-l-4 ${stat.borderColor} border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm`}
          >
            <div className={`p-3 rounded-2xl ${stat.bg} shadow-inner`}>
              <stat.icon className={stat.color} size={20} />
            </div>
            <div>
              <p className="text-2xl font-heading font-black text-slate-900 dark:text-white tracking-tighter">{stat.value}</p>
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-[0.2em]">{stat.label}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* TOOLBAR */}
      <div className="glass-panel p-4 flex flex-col md:flex-row items-center justify-between gap-4 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
         <div className="flex gap-2 w-full md:w-auto">
            {[
              { id: 'all', label: 'Tất cả' },
              { id: 'critical', label: 'Nguy cấp' },
              { id: 'warning', label: 'Cảnh báo' },
              { id: 'info', label: 'Thông tin' },
            ].map(f => (
              <button 
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-tight transition-all flex-1 md:flex-none border shadow-sm ${
                  activeFilter === f.id 
                    ? 'bg-indigo-600 text-white border-indigo-400 shadow-indigo-500/20' 
                    : 'bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
         </div>
         <div className="flex gap-2 w-full md:w-auto">
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-xl px-4 py-2.5 w-full md:w-64 focus-within:border-indigo-500/50 transition-all shadow-inner">
              <Search size={14} className="text-slate-400" />
              <input type="text" placeholder="Tìm ID, Cơ quan, Nội dung..." className="bg-transparent text-xs outline-none w-full text-slate-900 dark:text-white font-medium" />
            </div>
            <button className="p-2.5 border border-slate-200 dark:border-white/10 rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-all shadow-sm active:scale-95">
              <Filter size={16} />
            </button>
         </div>
      </div>

      {/* FEED TIMELINE */}
      <div className="glass-panel p-6 pb-2 min-h-[500px] border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm">
         <div className="space-y-4">
            <AnimatePresence mode="popLayout">
               {filterAlerts.map((alert, idx) => (
                  <motion.div 
                     layout
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     exit={{ opacity: 0, scale: 0.95, height: 0 }}
                     transition={{ duration: 0.2, delay: idx * 0.05 }}
                     key={alert.id}
                     className={`relative p-6 rounded-3xl border transition-all flex gap-6 group shadow-sm ${
                        alert.read 
                          ? 'bg-slate-100/50 dark:bg-white/5 border-slate-100 dark:border-white/5 opacity-70 hover:opacity-100'
                          : alert.type === 'critical'
                            ? 'bg-rose-50 dark:bg-rose-500/5 border-rose-200 dark:border-rose-500/20'
                            : alert.type === 'warning'
                              ? 'bg-amber-50 dark:bg-amber-500/5 border-amber-200 dark:border-amber-500/20'
                              : 'bg-cyan-50 dark:bg-cyan-500/5 border-cyan-200 dark:border-cyan-500/20'
                     }`}
                  >
                     {/* Unread dot */}
                     {!alert.read && (
                        <div className={`absolute top-6 right-6 w-3 h-3 rounded-full animate-pulse shadow-xl ${
                          alert.type === 'critical' ? 'bg-rose-500 shadow-rose-500/50' : 
                          alert.type === 'warning' ? 'bg-amber-500 shadow-amber-500/50' : 'bg-cyan-500 shadow-cyan-500/50'
                        }`} />
                     )}

                     <div className="shrink-0 pt-1">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-inner ${
                           alert.type === 'critical' ? 'bg-rose-500/10' : alert.type === 'warning' ? 'bg-amber-500/10' : 'bg-cyan-500/10'
                        }`}>
                           {getIcon(alert.type)}
                        </div>
                     </div>

                     <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                           <span className="text-[10px] font-black bg-white dark:bg-black/40 px-2.5 py-1 rounded-lg text-slate-500 dark:text-slate-500 border border-slate-200 dark:border-white/10 tracking-tighter shadow-sm">
                              {alert.id}
                           </span>
                           <span className="text-[11px] font-black text-slate-700 dark:text-slate-300 tracking-tight">
                              {alert.org}
                           </span>
                           <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold flex items-center gap-1.5 ml-auto mr-6 tracking-tight">
                              <Clock size={12} className="opacity-50" /> {alert.time}
                           </span>
                        </div>
                        
                        <h4 className={`text-lg font-heading font-black mb-1.5 tracking-tight ${
                          alert.type === 'critical' ? 'text-rose-600 dark:text-rose-400' : alert.type === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-cyan-600 dark:text-cyan-400'
                        }`}>
                           {alert.title}
                        </h4>
                        
                        <p className="text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed pr-10 font-medium">
                           {alert.message}
                        </p>
                        
                        {!alert.read && (
                           <div className="mt-5 flex gap-3">
                              {alert.type === 'critical' && (
                                 <button className="px-5 py-2 bg-rose-600 text-white rounded-xl text-[10px] font-black tracking-widest hover:bg-rose-700 transition-all shadow-[0_5px_15px_rgba(225,29,72,0.35)] active:scale-95">
                                    Xử lý ngay
                                 </button>
                              )}
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); setAlerts(alerts.map(a => a.id === alert.id ? { ...a, read: true } : a)); 
                                }}
                                className="px-5 py-2 bg-white dark:bg-white/10 text-slate-900 dark:text-slate-300 border border-slate-200 dark:border-white/10 rounded-xl text-[10px] font-black tracking-widest hover:bg-slate-50 dark:hover:bg-white/20 transition-all shadow-sm active:scale-95"
                              >
                                 Đánh dấu Đã đọc
                              </button>
                           </div>
                        )}
                     </div>

                     <button className="absolute right-5 bottom-5 p-2 text-slate-400 hover:text-indigo-600 dark:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-all shadow-sm active:scale-95">
                        <MoreVertical size={16} />
                     </button>
                  </motion.div>
               ))}
            </AnimatePresence>
            
            {filterAlerts.length === 0 && (
               <div className="py-24 flex flex-col items-center text-center opacity-40">
                  <div className="w-20 h-20 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center text-slate-400 mb-6 shadow-inner border border-slate-100 dark:border-white/5">
                    <CheckCircle2 size={40} />
                  </div>
                  <h3 className="text-slate-900 dark:text-white font-black tracking-[0.2em] mb-2 leading-relaxed">Không có cảnh báo mới</h3>
                  <p className="text-xs text-slate-500 font-bold tracking-tight">Hệ thống đang hoạt động an toàn ở trạng thái ổn định nhất.</p>
               </div>
            )}
         </div>
         
         {filterAlerts.length > 0 && (
            <div className="py-8 flex justify-center">
               <button className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-white tracking-[0.25em] transition-all bg-slate-50 dark:bg-white/5 px-6 py-2 rounded-full border border-slate-200 dark:border-white/5">
                  Tải thêm cảnh báo cũ
               </button>
            </div>
         )}
      </div>
    </div>
  );
}
